import { useCallback, useMemo, useState } from "react";
import { Button, Checkbox, type Column } from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import {
  APPLY_CHUNK_SIZE,
  BACKFILL_FIELDS,
  officeIdentityBackfillService,
  type ApplyRowResult,
  type ScanResponse,
  type ScanRow,
} from "../../../api/officeIdentityBackfill.service.js";
import { downloadPlannedCsv, downloadRollbackCsv } from "./backfillCsv.js";
import { ResultsTable, countByStatus } from "./ResultsTable.js";
import { RollbackSection } from "./RollbackSection.js";
import { runChunkedApply } from "./runApply.js";
import styles from "./OfficeIdentityBackfill.module.css";

/**
 * Backfills the office identity fields a UI bug wiped on address-book offices
 * (DEV-2803): the slug, country and ref columns plus the canonical
 * crm_entity_number external reference. The scan
 * endpoint (wp-syncer) finds the wiped fields and proposes each one's last-good
 * value from WordPress; apply (address-book) writes them back.
 *
 * The flow is deliberately step-by-step: scan is read-only and shows every
 * proposal first, the planned changes can — and should — be saved to CSV before
 * anything is written, and apply is a separate, explicit, confirmed step. The
 * run is chunked (APPLY_CHUNK_SIZE) because each row's pre-update value comes
 * back only in the apply response — the authoritative rollback CSV is built
 * from those responses, so a single oversized request that timed out would
 * lose the undo along with the run. A chunk that fails stops the run, and
 * everything the earlier chunks returned is kept and still downloadable.
 *
 * Rows WordPress cannot resolve (`proposed: null`) are shown for the
 * record but can never be selected or applied. Everything resolvable starts
 * selected — the wiped state is the bug, so the safe default is to fix it.
 */

type Phase = "idle" | "scanning" | "scanned" | "applying" | "done";

type ApplyProgress = {
  done: number;
  total: number;
};

type TableRow = {
  id: string;
  index: number;
  position: number;
  officeId: string;
  officeName: string;
  field: string;
  current: string | null;
  proposed: string | null;
  selectable: boolean;
  selected: boolean;
};

/**
 * The header checkbox, which is tri-state: some-but-not-all selected reads as
 * indeterminate. The design system's Checkbox has no prop for that — it is a
 * DOM property, not an attribute — so it is set through the forwarded ref.
 */
function SelectAllCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: () => void;
}) {
  const setInput = useCallback(
    (input: HTMLInputElement | null) => {
      if (input) {
        input.indeterminate = indeterminate;
      }
    },
    [indeterminate],
  );
  return (
    <Checkbox
      ref={setInput}
      aria-label="Select all resolvable rows"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

export default function OfficeIdentityBackfill() {
  const { hasPermission } = usePermissions();

  const [phase, setPhase] = useState<Phase>("idle");
  const [scan, setScan] = useState<ScanResponse | null>(null);
  /** Indexes into scan.rows. The scan returns one row per office+field. */
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [applyProgress, setApplyProgress] = useState<ApplyProgress>({ done: 0, total: 0 });
  /** The applied run's results, kept for the rollback download. */
  const [applied, setApplied] = useState<ApplyRowResult[] | null>(null);
  /**
   * Whether the planned-changes CSV has been taken for the current scan.
   * Only drives the confirmation dialog's nudge — it never blocks applying.
   */
  const [hasPlannedCsv, setHasPlannedCsv] = useState(false);

  // A developer tool with a bulk blast radius, gated on developer.write like
  // its Developer-section neighbours (Website Relabel, CRM webhook tester).
  const canUse = hasPermission("developer.write");

  const runScan = useCallback(async () => {
    setError(null);
    setApplied(null);
    setHasPlannedCsv(false);
    setScan(null);
    setSelected(new Set());
    setPhase("scanning");
    try {
      const response = await officeIdentityBackfillService.scan();
      setScan(response);
      // Every resolvable row starts selected — the wiped state is the bug.
      setSelected(
        new Set(response.rows.flatMap((row, index) => (row.proposed !== null ? [index] : []))),
      );
      setPhase("scanned");
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : String(scanError));
      setPhase("idle");
    }
  }, []);

  const toggleRow = useCallback((index: number) => {
    setSelected(current => {
      const next = new Set(current);
      if (!next.delete(index)) {
        next.add(index);
      }
      return next;
    });
  }, []);

  const setRows = useCallback((indexes: number[], isSelected: boolean) => {
    setSelected(current => {
      const next = new Set(current);
      for (const index of indexes) {
        if (isSelected) {
          next.add(index);
        } else {
          next.delete(index);
        }
      }
      return next;
    });
  }, []);

  const scanRows = scan?.rows ?? [];

  /** The rows this run will write: resolvable and still ticked. */
  const selectedRows = useMemo(
    () => scanRows.filter((row, index) => row.proposed !== null && selected.has(index)),
    [scanRows, selected],
  );
  const selectedCount = selectedRows.length;
  const resolvableCount = useMemo(
    () => scanRows.filter(row => row.proposed !== null).length,
    [scanRows],
  );

  /**
   * Hands the reviewer the planned-changes file BEFORE the run — the hard
   * requirement of this tool. It records office, field, current and proposed
   * for every selected row, so a tab that dies mid-apply still leaves a record
   * of what the run was doing.
   */
  const downloadPlanned = useCallback(() => {
    downloadPlannedCsv(selectedRows, new Date());
    setHasPlannedCsv(true);
  }, [selectedRows]);

  const applyBackfill = useCallback(async () => {
    setIsConfirming(false);
    setError(null);
    setPhase("applying");

    const { results, failure } = await runChunkedApply(
      // `proposed` is non-null by construction of selectedRows.
      selectedRows.map(row => ({
        officeId: row.officeId,
        field: row.field,
        value: row.proposed as string,
      })),
      "backfill",
      (done, total) => setApplyProgress({ done, total }),
    );

    if (results.length === 0 && failure) {
      // Nothing came back at all, so there is no rollback data to protect and
      // no result worth showing: back to the preview with the selection intact.
      setError(failure);
      setPhase("scanned");
      return;
    }
    setApplied(results);
    setError(failure);
    setPhase("done");
  }, [selectedRows]);

  /** Office names for the result table, keyed the only way results are: office+field. */
  const nameByKey = useMemo(() => {
    const names = new Map<string, string>();
    for (const row of scanRows) {
      names.set(`${row.officeId}:${row.field}`, row.officeName);
    }
    return names;
  }, [scanRows]);

  const nameFor = useCallback(
    (result: ApplyRowResult) => nameByKey.get(`${result.officeId}:${result.field}`) ?? null,
    [nameByKey],
  );

  const tableRows = useMemo<TableRow[]>(
    () =>
      scanRows.map((row: ScanRow, index) => ({
        id: `${index}-${row.officeId}-${row.field}`,
        index,
        position: index + 1,
        officeId: row.officeId,
        officeName: row.officeName,
        field: row.field,
        current: row.current,
        proposed: row.proposed,
        selectable: row.proposed !== null,
        selected: selected.has(index),
      })),
    [scanRows, selected],
  );

  const selectableRows = useMemo(() => tableRows.filter(row => row.selectable), [tableRows]);
  const allSelected = selectableRows.length > 0 && selectedCount === selectableRows.length;

  const columns = useMemo<Column<TableRow>[]>(
    () => [
      {
        header: (
          <SelectAllCheckbox
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            disabled={selectableRows.length === 0}
            onChange={() =>
              setRows(
                selectableRows.map(row => row.index),
                !allSelected,
              )
            }
          />
        ) as unknown as string,
        accessor: "selected",
        render: (_value, row) =>
          row.selectable ? (
            <Checkbox
              aria-label={`Backfill row ${row.position} — ${row.officeName} (${row.field})`}
              checked={row.selected}
              onChange={() => toggleRow(row.index)}
            />
          ) : (
            <span className={styles.mono} aria-hidden="true">
              —
            </span>
          ),
      },
      {
        header: "Office",
        accessor: "officeName",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.officeName || "—"}</span>,
      },
      {
        header: "Office id",
        accessor: "officeId",
        render: (_value, row) => (
          <span className={styles.mono} title={row.officeId}>
            {row.officeId.slice(0, 8)}
          </span>
        ),
      },
      {
        header: "Field",
        accessor: "field",
        render: (_value, row) => <span className={styles.mono}>{row.field}</span>,
      },
      {
        header: "Current",
        accessor: "current",
        wrap: true,
        render: (_value, row) => <span className={styles.mono}>{row.current ?? "—"}</span>,
      },
      {
        header: "Proposed",
        accessor: "proposed",
        wrap: true,
        render: (_value, row) =>
          row.proposed === null ? (
            <span className={`${styles.pill} ${styles.pillError}`}>unresolvable</span>
          ) : (
            <span className={styles.mono}>{row.proposed}</span>
          ),
      },
    ],
    [allSelected, selectedCount, selectableRows, setRows, toggleRow],
  );

  const resultCounts = useMemo(() => countByStatus(applied ?? []), [applied]);
  const appliedCount = resultCounts("applied");

  if (!canUse) {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied} role="alert">
          <h2>Access Denied</h2>
          <p>You do not have permission to view the Office Identity Backfill tool.</p>
        </div>
      </div>
    );
  }

  const isBusy = phase === "scanning" || phase === "applying";

  return (
    <div className={styles.page}>
      <PageHeader
        title="Office Identity Backfill"
        subtitle="Restore wiped office identity and country fields from WordPress"
      />

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardTitle}>1 · Scan for wiped fields</div>
        <div className={styles.toolbar}>
          <Button variant="contained" disabled={isBusy} onClick={() => void runScan()}>
            Scan
          </Button>
          <span className={styles.rowNote}>
            Read-only: finds offices with a wiped field and proposes the last-good value from the WP
            WordPress. Nothing is written until you apply.
          </span>
        </div>
      </div>

      {phase === "scanning" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Scanning…</div>
        </div>
      ) : null}

      {phase === "applying" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Applying…</div>
          <div className={styles.progress} role="status" aria-live="polite">
            {`${applyProgress.done.toLocaleString()} / ${applyProgress.total.toLocaleString()} rows`}
          </div>
          <div className={styles.rowNote}>
            {applyProgress.total > APPLY_CHUNK_SIZE
              ? `Sent in batches of ${APPLY_CHUNK_SIZE.toLocaleString()}. Keep this tab open — the rollback CSV is built from the responses.`
              : "Keep this tab open — the rollback CSV is built from the responses."}
          </div>
        </div>
      ) : null}

      {phase === "scanned" && scan ? (
        <>
          <div className={styles.card}>
            <div className={styles.cardTitle}>2 · Review planned changes</div>
            <div className={styles.tiles}>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{scan.counts.scanned}</div>
                <div className={styles.tileLabel}>Offices scanned</div>
              </div>
              {BACKFILL_FIELDS.map(field => (
                <div key={field} className={`${styles.tile} ${styles.tileNeutral}`}>
                  <div className={styles.tileNumber}>{scan.counts.missingByField[field] ?? 0}</div>
                  <div className={styles.tileLabel}>Missing {field}</div>
                </div>
              ))}
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{scan.counts.unresolved}</div>
                <div className={styles.tileLabel}>Unresolvable</div>
              </div>
              <div className={`${styles.tile} ${styles.tileOk}`}>
                <div className={styles.tileNumber}>{selectedCount}</div>
                <div className={styles.tileLabel}>Selected — will be backfilled</div>
              </div>
            </div>
          </div>

          {scan.counts.unresolved > 0 ? (
            <div className={`${styles.banner} ${styles.bannerWarning}`}>
              {scan.counts.unresolved} row{scan.counts.unresolved === 1 ? "" : "s"} have no
              last-good value in WordPress — shown for the record, excluded from the run.
            </div>
          ) : null}

          <div className={styles.stack}>
            <div className={styles.toolbar}>
              <span className={styles.resultCount} aria-live="polite">
                {selectedCount} of {resolvableCount} resolvable row
                {resolvableCount === 1 ? "" : "s"} selected
              </span>
            </div>
            <DataTable data={tableRows} columns={columns} emptyMessage="Nothing to backfill." />
          </div>

          <div className={styles.toolbar}>
            <Button variant="contained" disabled={selectedCount === 0} onClick={downloadPlanned}>
              Download planned changes (CSV)
            </Button>
            <span className={styles.rowNote}>
              Save this before applying. Saves as{" "}
              <code>office-identity-backfill-planned-….csv</code>.
            </span>
            <span className={styles.spacer} />
            <Button
              variant="contained"
              disabled={selectedCount === 0 || isBusy}
              onClick={() => setIsConfirming(true)}
            >
              Apply backfill ({selectedCount})
            </Button>
          </div>
        </>
      ) : null}

      {phase === "done" && applied ? (
        <>
          {resultCounts("error") > 0 ? (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              {resultCounts("error")} row{resultCounts("error") === 1 ? "" : "s"} failed to apply.
              Download the rollback CSV before retrying.
            </div>
          ) : null}

          <div className={styles.card}>
            <div className={styles.cardTitle}>Result</div>
            <div className={styles.tiles}>
              <div className={`${styles.tile} ${styles.tileOk}`}>
                <div className={styles.tileNumber}>{appliedCount}</div>
                <div className={styles.tileLabel}>Applied</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{resultCounts("noop")}</div>
                <div className={styles.tileLabel}>No-op</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{resultCounts("skipped_not_empty")}</div>
                <div className={styles.tileLabel}>Skipped — not empty</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{resultCounts("invalid")}</div>
                <div className={styles.tileLabel}>Invalid</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{resultCounts("error")}</div>
                <div className={styles.tileLabel}>Error</div>
              </div>
            </div>
          </div>

          <ResultsTable results={applied} nameFor={nameFor} />

          <div className={styles.toolbar}>
            <Button variant="outlined" onClick={() => void runScan()}>
              Scan again
            </Button>
            <span className={styles.spacer} />
            <Button
              variant="contained"
              disabled={appliedCount === 0}
              onClick={() => {
                if (applied) {
                  downloadRollbackCsv(applied, new Date());
                }
              }}
            >
              Download rollback CSV
            </Button>
          </div>
        </>
      ) : null}

      <RollbackSection />

      {isConfirming ? (
        <ConfirmationModal
          title="Apply office identity backfill"
          message={`${selectedCount} office field value${selectedCount === 1 ? "" : "s"} will be written.`}
          warningMessage={
            hasPlannedCsv
              ? "A rollback CSV is offered after the run. It is the only undo."
              : "No planned-changes CSV downloaded. The rollback CSV after the run is the only undo."
          }
          confirmButtonText={`Apply ${selectedCount} change${selectedCount === 1 ? "" : "s"}`}
          // A stray click beside the dialog must not stand in for a decision
          // about hundreds of office rows — Cancel and Escape still do.
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={applyBackfill}
        >
          {/* A nudge, never a gate: an operator who knows what they are doing
              can still confirm without taking the planned file. */}
          {hasPlannedCsv ? null : (
            <Button variant="outlined" onClick={downloadPlanned}>
              Download planned changes first
            </Button>
          )}
        </ConfirmationModal>
      ) : null}
    </div>
  );
}
